import { SECTION_TYPE_TABLE_SECTION, SECTION_TYPE_BLANK_SECTION, formCategories } from "@Helpers";
import * as math from "mathjs";
import type { useTranslation } from "@i18n";

/**
 * `useTranslation()`'s `t`, as every factory in this module receives it — validated keys in,
 * branded `TranslatedString` out. Taken from the hook itself so the two can never drift.
 */
export type TranslateFn = ReturnType<typeof useTranslation>["t"];

export const optionsViewType = {
  LIST: "list",
  DROPDOWN: "dropDown",
  CHIP: "chip",
  CARD: "card",
};

export const getUserCardFields = (t: TranslateFn) => [
  { name: t("First Name"), id: "firstName" },
  { name: t("Last Name"), id: "lastName" },
  { name: t("Designation"), id: "designation" },
  { name: t("Location"), id: "location" },
  { name: t("Contact No"), id: "contactNo" },
  { name: t("Email"), id: "email" },
  { name: t("Org"), id: "org" },
  { name: t("Signature"), id: "signature" },
  { name: t("User ID"), id: "userId" },
];

export const getUserValueFields = (resourceType: string, t: TranslateFn) => [
  {
    name: t("Selection Type"),
    id: "selectionType",
    subFields: [
      { name: t("Single User"), id: "singleUser" },
      { name: t("Multiple User"), id: "multipleUser" },
    ],
  },
  {
    name: t("Selection Scope"),
    id: "selectionScope",
    subFields: [
      ...([
        formCategories.projects,
        formCategories.issues,
        formCategories.files,
        formCategories.mailBox,
        formCategories.model,
        formCategories.reviews,
        formCategories.transmittal,
        formCategories.contract,
      ].includes(resourceType)
        ? []
        : [
            {
              name: t("Current Section"),
              id: "currentSection",
            },
            {
              name: t("Entire Form"),
              id: "entireForm",
            },
          ]),
      ...([
        formCategories.projects,
        formCategories.mailBox,
        formCategories.model,
        formCategories.reviews,
        formCategories.contract,
      ].includes(resourceType)
        ? []
        : [
            {
              name: t("Space"),
              id: "space",
            },
          ]),
      {
        name: t("Project"),
        id: "project",
      },
    ],
  },
  {
    name: t("Show As"),
    id: "viewType",
    subFields: [
      { name: t("Chip"), id: optionsViewType.CHIP },
      { name: t("Card"), id: optionsViewType.CARD },
    ],
  },
];
export const fileUploadSources = {
  FILE_MANAGER: "FILE_MANAGER",
  LOCAL: "LOCAL",
};
export const getFileUploadSourceOptions = (t: TranslateFn) => [
  {
    name: t("Local Storage"),
    value: fileUploadSources.LOCAL,
  },
  {
    name: t("File Manager"),
    value: fileUploadSources.FILE_MANAGER,
  },
];

export const getGeoLocationValues = (t: TranslateFn) => [
  {
    name: t("Capture only manual location"),
    id: "allowOnlyManualSelection",
    allowIn: [SECTION_TYPE_BLANK_SECTION, SECTION_TYPE_TABLE_SECTION],
  },
  {
    name: t("Capture only live location"),
    id: "captureOnlyLiveLocation",
    allowIn: [SECTION_TYPE_BLANK_SECTION, SECTION_TYPE_TABLE_SECTION],
  },
  {
    name: t("Capture both live and manual location"),
    id: "allowBothSelection",
    allowIn: [SECTION_TYPE_BLANK_SECTION, SECTION_TYPE_TABLE_SECTION],
  },
];

export const fieldTypeToIconMap: Record<string, string> = {
  "single-line": "SingleLineTextBox",
  "multi-line": "MultiLineTextBox",
  "single-select": "RadioButtonIcon",
  "multi-select": "CheckBoxIcon",
  "date-time": "DateTimeIcon",
  "look-up": "LookUpIcon",
  number: "NumericIcon",
  users: "UserProfile",
  fileUpload: "upload",
  image: "image",
  url: "URL",
  geoLocation: "geoLocation",
  default: "ErrorIcon",
  formula: "Formula",
  concatenate: "merge",
  autoNumber: "autoNumber",
  space: "spaces",
  fileVersion: "Version",
  file: "files",
};
/**
 * A formula is only ever meant to be plain arithmetic over the mapped numeric
 * field variables. mathjs, however, can also parse ranges (`[1:1e9]`), matrices,
 * function calls (`factorial(n)`), and block/assignment expressions — any of which
 * can make `math.evaluate` run unbounded in time or memory and freeze the tab when
 * the stored formula is evaluated for every grid row.
 *
 * These bounds reject everything outside the arithmetic subset, and are enforced
 * both at save time ({@link validateFormula}) and before every evaluation
 * ({@link evaluateFormula}) so a hand-crafted/legacy stored expression can't slip
 * through. A matching (looser) guard exists server-side on `formulaExpression`.
 */
const FORMULA_MAX_LENGTH = 1000;
const FORMULA_ALLOWED_OPERATOR_FNS = new Set([
  "add",
  "subtract",
  "multiply",
  "divide",
  "mod",
  "pow",
  "unaryMinus",
  "unaryPlus",
]);
const FORMULA_MAX_POW_EXPONENT = 1024;

/**
 * The parsed-node members this walker reads. mathjs's `MathNode` declares only `type` / `isNode` /
 * `comment`, so the per-kind members — `fn` and `args` on an OperatorNode, `value` on a
 * ConstantNode, `name` / `isSymbolNode` on a SymbolNode — are named here as optional. Purely
 * structural: nothing is constructed against it, it only re-opens mathjs's declaration.
 */
type ParsedFormulaNode = math.MathNode & {
  fn?: string;
  args?: ParsedFormulaNode[];
  value?: unknown;
  name?: string;
  isSymbolNode?: boolean;
};

/**
 * Walks the parsed expression and returns false if it contains anything other
 * than constants, symbols, parentheses, and the whitelisted arithmetic
 * operators (with a bounded literal exponent for `^`).
 */
const isFormulaNodeSafe = (node: math.MathNode): boolean => {
  let safe = true;
  node.traverse((child: ParsedFormulaNode) => {
    if (!safe) return;
    switch (child.type) {
      case "ConstantNode":
      case "SymbolNode":
      case "ParenthesisNode":
        return;
      case "OperatorNode": {
        // The switch above already established this is an OperatorNode, which always carries `fn`.
        if (!FORMULA_ALLOWED_OPERATOR_FNS.has(child.fn!)) {
          safe = false;
          return;
        }
        if (child.fn === "pow") {
          const exponent = child.args?.[1];
          if (exponent?.type === "ConstantNode" && Math.abs(Number(exponent.value)) > FORMULA_MAX_POW_EXPONENT) {
            safe = false;
          }
        }
        return;
      }
      default:
        safe = false;
    }
  });
  return safe;
};

/**
 * A `catch` binding is `unknown`, so the thrown value has to be narrowed before its message can
 * be read. mathjs raises real `Error` subclasses (`SyntaxError`, `TypeError`, ...); anything else
 * yields "" so the caller falls back to the generic translated message.
 */
const formulaErrorMessage = (e: unknown): string => (e instanceof Error ? e.message : "");

export const validateFormula = (value: string, allowedVars: string[], t: TranslateFn): string => {
  if (!value.trim()) return "";
  if (value.length > FORMULA_MAX_LENGTH) return t("Invalid formula");
  let node: math.MathNode;
  try {
    node = math.parse(value);
  } catch (e) {
    return formulaErrorMessage(e) || t("Invalid formula");
  }
  if (!isFormulaNodeSafe(node)) {
    return t("Invalid formula");
  }
  const allowed = new Set(allowedVars);
  const unknown = new Set<string>();
  node.traverse((child: ParsedFormulaNode) => {
    // `isSymbolNode` is only set on a SymbolNode, which always carries `name`.
    if (child.isSymbolNode && !allowed.has(child.name!)) {
      unknown.add(child.name!);
    }
  });
  if (unknown.size) {
    return t("Unknown variable(s): {{vars}}", { vars: Array.from(unknown).join(", ") });
  }
  const scope: Record<string, number> = {};
  allowedVars.forEach((v) => (scope[v] = 1));
  try {
    node.evaluate(scope);
  } catch (e) {
    return formulaErrorMessage(e) || t("Invalid formula");
  }
  return "";
};

/**
 * Evaluate a formula expression against a variable scope.
 *
 * Returns the numeric result, or `null` when the expression is empty, throws,
 * or produces a non-finite value (Infinity / NaN). The caller is expected to
 * have validated the expression first via {@link validateFormula}.
 *
 * @param value     The formula string (e.g. "(A + B) / C").
 * @param scope     Variable-name → numeric-value mapping (e.g. { A: 10, B: 20, C: 5 }).
 */
export const evaluateFormula = (value: string, scope: Record<string, number>): number | null => {
  if (!value.trim()) return null;
  if (value.length > FORMULA_MAX_LENGTH) return null;
  try {
    const node = math.parse(value);
    // Defence-in-depth: never evaluate an expression that could be unbounded,
    // even if it was somehow stored without passing validateFormula.
    if (!isFormulaNodeSafe(node)) return null;
    const result = node.evaluate(scope);
    return typeof result === "number" && Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
};
